import React, { useState, useEffect } from 'react';
import { XMarkIcon, CurrencyDollarIcon, CalculatorIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';

interface PricingData {
  asking_price?: number;
  wholesale_value?: number;
  trade_in_value?: number;
  retail_value?: number;
  acquisition_cost?: number;
  total_cost?: number;
}

interface Vehicle {
  vehicle_id: string;
  vin: string;
  year: number;
  make: string;
  model: string;
  asking_price?: number;
  wholesale_value?: number;
  trade_in_value?: number;
  retail_value?: number;
  acquisition_cost?: number;
  total_cost?: number;
  estimated_profit?: number;
  profit_margin?: number;
}

interface PriceAdjustmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  vehicle: Vehicle;
  onSave: (vehicleId: string, pricingData: PricingData) => Promise<void>;
  loading?: boolean;
}

const PriceAdjustmentModal: React.FC<PriceAdjustmentModalProps> = ({
  isOpen,
  onClose,
  vehicle,
  onSave,
  loading = false,
}) => {
  const [prices, setPrices] = useState<PricingData>({
    asking_price: vehicle.asking_price || 0,
    wholesale_value: vehicle.wholesale_value || 0,
    trade_in_value: vehicle.trade_in_value || 0,
    retail_value: vehicle.retail_value || 0,
  });

  const [calculatedValues, setCalculatedValues] = useState({
    estimated_profit: 0,
    profit_margin: 0,
  });

  useEffect(() => {
    if (isOpen) {
      setPrices({
        asking_price: vehicle.asking_price || 0,
        wholesale_value: vehicle.wholesale_value || 0,
        trade_in_value: vehicle.trade_in_value || 0,
        retail_value: vehicle.retail_value || 0,
      });
    }
  }, [isOpen, vehicle]);

  useEffect(() => {
    // Recalculate profit metrics whenever prices change
    const totalCost = vehicle.total_cost || vehicle.acquisition_cost || 0;
    const askingPrice = prices.asking_price || 0;

    if (askingPrice > 0 && totalCost > 0) {
      const estimatedProfit = askingPrice - totalCost;
      const profitMargin = (estimatedProfit / askingPrice) * 100;

      setCalculatedValues({
        estimated_profit: estimatedProfit,
        profit_margin: profitMargin,
      });
    } else {
      setCalculatedValues({ estimated_profit: 0, profit_margin: 0 });
    }
  }, [prices.asking_price, vehicle.total_cost, vehicle.acquisition_cost]);

  const handlePriceChange = (field: keyof PricingData, value: string) => {
    const numValue = parseFloat(value) || 0;
    setPrices((prev) => ({ ...prev, [field]: numValue }));
  };

  const handleQuickAdjustment = (field: keyof PricingData, percentage: number) => {
    const currentValue = prices[field] || 0;
    const adjustment = currentValue * (percentage / 100);
    const newValue = Math.max(0, currentValue + adjustment);
    setPrices((prev) => ({ ...prev, [field]: newValue }));
  };

  const handleSave = async () => {
    try {
      await onSave(vehicle.vehicle_id, prices);
      toast.success('Pricing updated successfully');
      onClose();
    } catch (error) {
      toast.error('Failed to update pricing');
      console.error('Error updating pricing:', error);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getProfitColor = (profit: number) => {
    return profit > 0 ? 'text-green-600' : 'text-red-600';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Adjust Pricing</h2>
            <p className="text-sm text-gray-500">
              {vehicle.year} {vehicle.make} {vehicle.model} • VIN: {vehicle.vin.slice(-8)}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Asking Price */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">Asking Price</label>
            <div className="flex items-center space-x-2">
              <div className="relative flex-1">
                <CurrencyDollarIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <input
                  type="number"
                  value={prices.asking_price || ''}
                  onChange={(e) => handlePriceChange('asking_price', e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0"
                />
              </div>
              <div className="flex space-x-1">
                <button
                  onClick={() => handleQuickAdjustment('asking_price', -10)}
                  className="px-2 py-1 text-xs bg-red-50 text-red-700 rounded hover:bg-red-100"
                  type="button"
                >
                  -10%
                </button>
                <button
                  onClick={() => handleQuickAdjustment('asking_price', -5)}
                  className="px-2 py-1 text-xs bg-red-50 text-red-700 rounded hover:bg-red-100"
                  type="button"
                >
                  -5%
                </button>
                <button
                  onClick={() => handleQuickAdjustment('asking_price', 5)}
                  className="px-2 py-1 text-xs bg-green-50 text-green-700 rounded hover:bg-green-100"
                  type="button"
                >
                  +5%
                </button>
                <button
                  onClick={() => handleQuickAdjustment('asking_price', 10)}
                  className="px-2 py-1 text-xs bg-green-50 text-green-700 rounded hover:bg-green-100"
                  type="button"
                >
                  +10%
                </button>
              </div>
            </div>
          </div>

          {/* Market Values Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Wholesale Value</label>
              <div className="relative">
                <CurrencyDollarIcon className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <input
                  type="number"
                  value={prices.wholesale_value || ''}
                  onChange={(e) => handlePriceChange('wholesale_value', e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="0"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Trade-in Value</label>
              <div className="relative">
                <CurrencyDollarIcon className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <input
                  type="number"
                  value={prices.trade_in_value || ''}
                  onChange={(e) => handlePriceChange('trade_in_value', e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="0"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Retail Value</label>
              <div className="relative">
                <CurrencyDollarIcon className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <input
                  type="number"
                  value={prices.retail_value || ''}
                  onChange={(e) => handlePriceChange('retail_value', e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="0"
                />
              </div>
            </div>
          </div>

          {/* Profit Calculation */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <div className="flex items-center space-x-2">
              <CalculatorIcon className="w-5 h-5 text-gray-600" />
              <h3 className="text-sm font-medium text-gray-900">Profit Analysis</h3>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Total Cost:</span>
                <span className="font-medium">
                  {formatCurrency(vehicle.total_cost || vehicle.acquisition_cost || 0)}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-600">Asking Price:</span>
                <span className="font-medium">{formatCurrency(prices.asking_price || 0)}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-600">Estimated Profit:</span>
                <span
                  className={`font-semibold ${getProfitColor(calculatedValues.estimated_profit)}`}
                >
                  {formatCurrency(calculatedValues.estimated_profit)}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-600">Profit Margin:</span>
                <span
                  className={`font-semibold ${getProfitColor(calculatedValues.estimated_profit)}`}
                >
                  {calculatedValues.profit_margin.toFixed(1)}%
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PriceAdjustmentModal;
